import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LabelIcon from "@mui/icons-material/Label";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Driver, DriverGroup, LabelRule } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";
import ConfirmDialog from "../dialogs/ConfirmDialog";

// ── Create Group Dialog ────────────────────────────────────────────────────────

function CreateGroupDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api(`${MGMT}/driver-groups`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      toast.show("Group created");
      setName("");
      setDescription("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Driver Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Manage Members Dialog ──────────────────────────────────────────────────────

function ManageMembersDialog({
  open,
  group,
  drivers,
  onClose,
  onSaved,
}: {
  open: boolean;
  group: DriverGroup | null;
  drivers: Driver[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState<number | null>(null);
  const toast = useToast();

  const memberIds = new Set((group?.drivers ?? []).map((d) => d.id));
  const dynamicIds = new Set((group?.drivers ?? []).filter((d) => d.is_dynamic).map((d) => d.id));

  const filtered = drivers.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.email.toLowerCase().includes(search.toLowerCase())
  );

  const add = async (driver: Driver) => {
    if (!group) return;
    setSaving(driver.id);
    try {
      await api(`${MGMT}/drivers/${driver.id}/groups/${group.id}`, { method: "POST" });
      toast.show(`${driver.name} added`);
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(null);
    }
  };

  const remove = async (driver: Driver) => {
    if (!group) return;
    setSaving(driver.id);
    try {
      await api(`${MGMT}/drivers/${driver.id}/groups/${group.id}`, { method: "DELETE" });
      toast.show(`${driver.name} removed`);
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Members: "{group?.name}"</DialogTitle>
      <DialogContent>
        <TextField
          label="Search drivers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
        />
        <TableContainer>
          <Table size="small">
            <TableBody>
              {filtered.map((d) => {
                const isMember = memberIds.has(d.id);
                return (
                  <TableRow key={d.id} sx={{ bgcolor: isMember ? "action.selected" : undefined }}>
                    <TableCell>{d.name}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{d.email}</TableCell>
                    <TableCell>
                      {isMember && !dynamicIds.has(d.id) && <Chip label="explicit" size="small" color="primary" variant="outlined" />}
                      {dynamicIds.has(d.id) && <Chip label="dynamic" size="small" color="info" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">
                      {dynamicIds.has(d.id) ? (
                        <Button size="small" disabled title="Dynamic member via label rules">
                          Auto
                        </Button>
                      ) : isMember ? (
                        <Button size="small" color="error" onClick={() => remove(d)} disabled={saving === d.id}>
                          Remove
                        </Button>
                      ) : (
                        <Button size="small" onClick={() => add(d)} disabled={saving === d.id}>
                          Add
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Label Rules Dialog ─────────────────────────────────────────────────────────

function LabelRulesDialog({
  open,
  group,
  onClose,
  onSaved,
}: {
  open: boolean;
  group: DriverGroup | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [rules, setRules] = useState<LabelRule[]>([]);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (open && group) {
      setRules(group.label_rules ?? []);
    }
  }, [open, group]);

  const addRule = async () => {
    if (!group || !key.trim() || !value.trim()) return;
    setSaving(true);
    try {
      const rule = await api<LabelRule>(`${MGMT}/driver-groups/${group.id}/label-rules`, {
        method: "POST",
        body: JSON.stringify({ key: key.trim(), value: value.trim() }),
      });
      if (rule) setRules((prev) => [...prev, rule]);
      setKey("");
      setValue("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId: number) => {
    if (!group) return;
    setDeletingId(ruleId);
    try {
      await api(`${MGMT}/driver-groups/${group.id}/label-rules/${ruleId}`, { method: "DELETE" });
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Label Rules: "{group?.name}"</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drivers matching ALL rules below are dynamically added to this group.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
          <TextField
            label="Key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={addRule}
            disabled={saving || !key.trim() || !value.trim()}
            size="small"
          >
            Add
          </Button>
        </Stack>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {rules.length === 0 && (
            <Typography variant="body2" color="text.secondary">No label rules yet.</Typography>
          )}
          {rules.map((r) => (
            <Chip
              key={r.id}
              label={`${r.key}=${r.value}`}
              onDelete={() => deleteRule(r.id)}
              disabled={deletingId === r.id}
              color="info"
              variant="outlined"
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main View ──────────────────────────────────────────────────────────────────

export default function DriverGroups() {
  const api = useAuthApi();
  const [groups, setGroups] = useState<DriverGroup[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DriverGroup | null>(null);
  const [managingMembersOf, setManagingMembersOf] = useState<DriverGroup | null>(null);
  const [managingRulesOf, setManagingRulesOf] = useState<DriverGroup | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([
        api<DriverGroup[]>(`${MGMT}/driver-groups`),
        api<DriverListResponse>(`${MGMT}/drivers`),
      ]);
      setGroups(g ?? []);
      setDrivers(fullDriversFromResponse(d));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // After any mutation, reload and re-sync the open dialog's group data
  const reloadAndSync = useCallback(async () => {
    const [g, d] = await Promise.all([
      api<DriverGroup[]>(`${MGMT}/driver-groups`),
      api<DriverListResponse>(`${MGMT}/drivers`),
    ]).catch(() => [[], []] as [DriverGroup[], Driver[]]);
    const groups = g ?? [];
    const drivers = fullDriversFromResponse(d);
    setGroups(groups);
    setDrivers(drivers);
    setManagingMembersOf((prev) => prev ? (groups.find((gr) => gr.id === prev.id) ?? null) : null);
    setManagingRulesOf((prev) => prev ? (groups.find((gr) => gr.id === prev.id) ?? null) : null);
  }, []);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/driver-groups/${deleting.id}`, { method: "DELETE" });
      toast.show("Group deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      [g.name, g.description].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [groups, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New Group
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter driver groups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Label Rules</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No driver groups match the current filters." : "No driver groups yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((g) => (
              <TableRow key={g.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{g.description ?? "–"}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={g.drivers.length}
                    size="small"
                    color={g.drivers.length > 0 ? "primary" : "default"}
                    variant="outlined"
                    onClick={() => setManagingMembersOf(g)}
                    clickable
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={g.label_rules.length > 0 ? `${g.label_rules.length} rule${g.label_rules.length > 1 ? "s" : ""}` : "none"}
                    size="small"
                    color={g.label_rules.length > 0 ? "info" : "default"}
                    variant="outlined"
                    onClick={() => setManagingRulesOf(g)}
                    clickable
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Manage members" onClick={() => setManagingMembersOf(g)}>
                    <GroupAddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Label rules" onClick={() => setManagingRulesOf(g)}>
                    <LabelIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(g)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateGroupDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }}
      />

      <ManageMembersDialog
        open={managingMembersOf !== null}
        group={managingMembersOf}
        drivers={drivers}
        onClose={() => setManagingMembersOf(null)}
        onSaved={reloadAndSync}
      />

      <LabelRulesDialog
        open={managingRulesOf !== null}
        group={managingRulesOf}
        onClose={() => setManagingRulesOf(null)}
        onSaved={reloadAndSync}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete group?"
        message={`Delete group "${deleting?.name}"? Members will not be deleted.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
